import { forwardRef } from 'react';
import { Link as RouterLink, LinkProps as RouterLinkProps } from 'react-router-dom';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const linkVariants = cva(
  'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring',
  {
    variants: {
      variant: {
        // Link-style variants
        default: 'text-primary hover:text-primary/90 underline-offset-4 hover:underline',
        'link-primary': 'text-primary hover:text-primary/90',
        'link-muted': 'text-muted hover:text-foreground',

        // Button-style variants (match Button component)
        primary: 'inline-flex items-center justify-center rounded-md font-medium bg-primary text-white hover:bg-primary/90',
        secondary: 'inline-flex items-center justify-center rounded-md font-medium bg-secondary text-white hover:bg-secondary/90',
        danger: 'inline-flex items-center justify-center rounded-md font-medium bg-danger text-white hover:bg-danger/90',
        ghost: 'inline-flex items-center justify-center rounded-md font-medium hover:bg-muted/10 text-foreground',
        outline: 'inline-flex items-center justify-center rounded-md font-medium border border-border text-foreground hover:bg-surface-1',
      },
      size: {
        xs: 'px-2 py-0.5 text-xs',
        sm: 'h-9 px-3 text-sm',
        md: 'h-10 px-4 text-base',
        lg: 'h-11 px-8 text-lg',
        compact: 'px-3 py-1 text-sm',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface LinkProps
  extends RouterLinkProps,
    VariantProps<typeof linkVariants> {}

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ variant, size, className, ...props }, ref) => {
    return (
      <RouterLink
        ref={ref}
        className={cn(linkVariants({ variant, size }), className)}
        {...props}
      />
    );
  }
);

Link.displayName = 'Link';
